import React, { useState, useEffect } from 'react';
import MainLayout from '../components/layout/MainLayout';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import theme from '../config/theme';
import '../config/pastelTheme.css';

// Registrar componentes do Chart.js
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const Dashboard = () => {
  const [userData, setUserData] = useState({});
  const [loading, setLoading] = useState(true);
  const [recentSimulation, setRecentSimulation] = useState(null);
  const [investmentData, setInvestmentData] = useState({
    balance: 0,
    totalInvested: 0,
    totalProfit: 0,
    monthlyProfit: 0
  });
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: []
  });

  useEffect(() => {
    // Carregar dados do usuário
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setUserData(user);
    
    // Buscar dados de investimento
    fetchInvestmentData();
    
    // Buscar simulação mais recente
    fetchRecentSimulation();
    
    // Gerar dados do gráfico (simulados para demonstração)
    generateChartData();
    
    setLoading(false);
  }, []);

  const fetchInvestmentData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('Token não encontrado');
        return;
      }
      
      // Buscar resumo do investimento
      const response = await axios.get('/api/investment/summary', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Dados de resumo recebidos:', response.data);
      
      if (response.data && response.data.summary) {
        setInvestmentData({
          balance: response.data.summary.currentBalance || 0,
          totalInvested: response.data.summary.totalInvested || 0,
          totalProfit: response.data.summary.totalProfit || 0,
          monthlyProfit: response.data.summary.lastMonthProfit || 0
        });
      } else {
        // Caso não tenha dados, manter os valores atuais para demonstração
        setInvestmentData({
          balance: 15750.42,
          totalInvested: 12000,
          totalProfit: 3750.42,
          monthlyProfit: 450.25
        });
      }
    } catch (error) {
      console.error('Erro ao buscar dados de investimento:', error);
      // Manter valores de demonstração em caso de erro
      setInvestmentData({
        balance: 15750.42,
        totalInvested: 12000,
        totalProfit: 3750.42,
        monthlyProfit: 450.25
      });
    }
  };

  const fetchRecentSimulation = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('Token não encontrado');
        return;
      }
      
      // Buscar simulação mais recente
      const response = await axios.get('/api/simulation/recent', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Simulação recente:', response.data);
      
      if (response.data && response.data.simulation) {
        setRecentSimulation(response.data.simulation);
      } else {
        // Dados de demonstração se não houver simulações
        setRecentSimulation({
          id: 1,
          initialAmount: 10000,
          monthlyContribution: 500,
          interestRate: 0.8,
          period: 24,
          result: 23540.87,
          createdAt: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Erro ao buscar simulação recente:', error);
      // Dados de demonstração em caso de erro
      setRecentSimulation({
        id: 1,
        initialAmount: 10000,
        monthlyContribution: 500,
        interestRate: 0.8,
        period: 24,
        result: 23540.87,
        createdAt: new Date().toISOString()
      });
    }
  };

  const generateChartData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('Token não encontrado');
        setDefaultChartData();
        return;
      }
      
      // Buscar dados históricos para o gráfico
      const response = await axios.get('/api/investment/history', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Dados históricos:', response.data);
      
      if (response.data && response.data.history && response.data.history.length > 0) {
        const history = response.data.history;
        
        // Organizar os dados para o gráfico
        const labels = history.map(item => item.month);
        const investedValues = history.map(item => item.invested);
        const balanceValues = history.map(item => item.balance);
        
        setChartData({
          labels: labels,
          datasets: [
            {
              label: 'Valor Investido',
              data: investedValues,
              borderColor: theme.colors.gray,
              backgroundColor: 'rgba(160, 174, 192, 0.1)',
              tension: 0.4,
              fill: false
            },
            {
              label: 'Saldo Total',
              data: balanceValues,
              borderColor: theme.colors.primary,
              backgroundColor: theme.colors.primaryLight,
              tension: 0.4,
              fill: false
            }
          ]
        });
      } else {
        setDefaultChartData();
      }
    } catch (error) {
      console.error('Erro ao buscar dados históricos:', error);
      setDefaultChartData();
    }
  };
  
  const setDefaultChartData = () => {
    // Dados simulados para o gráfico caso não haja dados reais
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    const investedValues = [10000, 10500, 11000, 11500, 12000, 12000, 12000, 12000, 12000, 12000, 12000, 12000];
    const balanceValues = [10000, 10605, 11236, 11893, 12580, 12832, 13088, 13350, 13617, 13889, 14167, 14450];
    
    setChartData({
      labels: months,
      datasets: [
        {
          label: 'Valor Investido',
          data: investedValues,
          borderColor: theme.colors.gray,
          backgroundColor: 'rgba(160, 174, 192, 0.1)',
          tension: 0.4,
          fill: false
        },
        {
          label: 'Saldo Total',
          data: balanceValues,
          borderColor: theme.colors.primary,
          backgroundColor: theme.colors.primaryLight,
          tension: 0.4,
          fill: false
        }
      ]
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatPercentage = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value / 100);
  };

  // Estilo personalizado para os gráficos
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: {
            family: "'Inter', sans-serif",
            size: 12
          },
          usePointStyle: true,
          boxWidth: 6
        }
      },
      tooltip: {
        backgroundColor: '#fff',
        titleColor: theme.colors.dark,
        bodyColor: theme.colors.text,
        borderColor: theme.colors.border,
        borderWidth: 1,
        padding: 16,
        cornerRadius: 8,
        displayColors: false,
        titleFont: {
          family: "'Inter', sans-serif",
          size: 14,
          weight: '600'
        },
        bodyFont: {
          family: "'Inter', sans-serif",
          size: 13
        }
      },
      title: {
        display: true,
        text: 'Evolução do Investimento (12 meses)',
        font: {
          family: "'Inter', sans-serif",
          size: 16,
          weight: '600'
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif",
            size: 11
          },
          color: theme.colors.gray
        }
      },
      y: {
        beginAtZero: false,
        grid: {
          borderDash: [2, 2],
          color: theme.colors.grayLight
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif",
            size: 11
          },
          color: theme.colors.gray,
          callback: (value) => formatCurrency(value)
        }
      }
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
          <div className="spinner-border" style={{ color: theme.colors.primary }} role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="pastel-dashboard">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="pastel-heading">Dashboard</h1>
            <p className="pastel-subheading mb-0">
              Bem-vindo(a) de volta, {userData.name || 'Usuário'}!
            </p>
          </div>
          
          <div className="d-flex align-items-center">
            <button 
              className="pastel-btn pastel-btn-primary"
              onClick={() => window.location.href = '/setup'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2">
                <path d="M12 5v14M5 12h14" />
              </svg>
              Nova Simulação
            </button>
          </div>
        </div>
      
        {/* Cards de resumo */}
        <div className="row g-3 mb-4">
          {/* Card 1: Saldo Total - Azul Pastel */}
          <div className="col-md-3">
            <div className="pastel-card border-primary h-100">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <span className="pastel-label">Saldo Total</span>
                  <h2 className="pastel-value">
                    {formatCurrency(investmentData.balance)}
                  </h2>
                  <div className="d-flex align-items-center">
                    <span className="pastel-small">
                      Rendimento Mensal: {formatCurrency(investmentData.monthlyProfit)}
                    </span>
                  </div>
                </div>
                <div className="pastel-icon-box primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                  </svg>
                </div>
              </div>
            </div>
          </div>
          
          {/* Card 2: Total Investido - Verde Pastel */}
          <div className="col-md-3">
            <div className="pastel-card border-success h-100">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <span className="pastel-label">Total Investido</span>
                  <h2 className="pastel-value">
                    {formatCurrency(investmentData.totalInvested)}
                  </h2>
                  <div className="d-flex align-items-center">
                    <span className="pastel-small">
                      Lucro Total: {formatCurrency(investmentData.totalProfit)}
                    </span>
                  </div>
                </div>
                <div className="pastel-icon-box success">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                </div>
              </div>
            </div>
          </div>
          
          {/* Card 3: Rentabilidade - Lavanda */}
          <div className="col-md-3">
            <div className="pastel-card border-purple h-100">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <span className="pastel-label">Rentabilidade</span>
                  <h2 className="pastel-value">
                    {formatPercentage(investmentData.totalProfit / investmentData.totalInvested * 100)}
                  </h2>
                  <div className="d-flex align-items-center">
                    <span className="pastel-badge pastel-badge-purple me-2">
                      +31.25%
                    </span>
                    <span className="pastel-small">
                      Desde o início
                    </span>
                  </div>
                </div>
                <div className="pastel-icon-box purple">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                    <polyline points="17 6 23 6 23 12"></polyline>
                  </svg>
                </div>
              </div>
            </div>
          </div>
          
          {/* Card 4: Rentabilidade Mensal - Rosa */}
          <div className="col-md-3">
            <div className="pastel-card border-pink h-100">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <span className="pastel-label">Rentabilidade Mensal</span>
                  <h2 className="pastel-value">0.80%</h2>
                  <div className="d-flex align-items-center">
                    <span className="pastel-badge pastel-badge-pink me-2">
                      +0.05%
                    </span>
                    <span className="pastel-small">
                      Último mês
                    </span>
                  </div>
                </div>
                <div className="pastel-icon-box pink">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Gráfico */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="pastel-card">
              <div style={{ height: '350px' }}>
                <Line options={chartOptions} data={chartData} />
              </div>
            </div>
          </div>
        </div>
        
        {/* Simulação recente e ações rápidas */}
        <div className="row">
          <div className="col-lg-8">
            <div className="pastel-card h-100">
              <h3 className="pastel-heading mb-4">Simulação Recente</h3>
              
              {recentSimulation ? (
                <div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <span className="pastel-label">Valor Inicial</span>
                        <div className="pastel-value">{formatCurrency(recentSimulation.initialAmount)}</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <span className="pastel-label">Aporte Mensal</span>
                        <div className="pastel-value">{formatCurrency(recentSimulation.monthlyContribution)}</div>
                      </div>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <span className="pastel-label">Taxa de Juros</span>
                        <div className="pastel-value">{recentSimulation.interestRate}% ao mês</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <span className="pastel-label">Período</span>
                        <div className="pastel-value">{recentSimulation.period} meses</div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <span className="pastel-label">Resultado</span>
                        <div className="pastel-value" style={{ color: theme.colors.success }}>{formatCurrency(recentSimulation.result)}</div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4">
                    <button className="pastel-btn pastel-btn-primary me-2">
                      Ver Detalhes
                    </button>
                    <button className="pastel-btn pastel-btn-secondary">
                      Nova Simulação
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <div style={{ fontSize: '1.25rem', fontWeight: 600, color: theme.colors.dark, marginBottom: '12px' }}>
                    Nenhuma simulação recente
                  </div>
                  <div className="pastel-subheading mb-4">
                    Você ainda não realizou nenhuma simulação de investimento.
                  </div>
                  <button className="pastel-btn pastel-btn-primary">
                    Criar Primeira Simulação
                  </button>
                </div>
              )}
            </div>
          </div>
          
          <div className="col-lg-4">
            <div className="pastel-card h-100">
              <h3 className="pastel-heading mb-4">Ações Rápidas</h3>
              
              <div className="quick-actions">
                <a href="/setup" className="d-flex align-items-center p-3 mb-3" style={{ 
                  textDecoration: 'none', 
                  borderRadius: '12px', 
                  backgroundColor: theme.colors.primaryLight, 
                  color: theme.colors.dark,
                  transition: 'all 0.2s ease'
                }}>
                  <div className="pastel-icon-box primary me-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="12" rx="1" />
                      <line x1="7" y1="20" x2="17" y2="20" />
                      <line x1="9" y1="16" x2="9" y2="20" />
                      <line x1="15" y1="16" x2="15" y2="20" />
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>Nova Simulação</div>
                    <div className="pastel-small">Simule um novo investimento</div>
                  </div>
                </a>
                
                <a href="/reports" className="d-flex align-items-center p-3 mb-3" style={{ 
                  textDecoration: 'none', 
                  borderRadius: '12px', 
                  backgroundColor: theme.colors.successLight, 
                  color: theme.colors.dark,
                  transition: 'all 0.2s ease'
                }}>
                  <div className="pastel-icon-box success me-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <polyline points="10 9 9 9 8 9"></polyline>
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>Ver Relatórios</div>
                    <div className="pastel-small">Analise os resultados das simulações</div>
                  </div>
                </a>
                
                <a href="/profile" className="d-flex align-items-center p-3" style={{ 
                  textDecoration: 'none', 
                  borderRadius: '12px', 
                  backgroundColor: theme.colors.purpleLight, 
                  color: theme.colors.dark,
                  transition: 'all 0.2s ease'
                }}>
                  <div className="pastel-icon-box purple me-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>Meu Perfil</div>
                    <div className="pastel-small">Gerencie suas configurações</div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Dashboard; 